import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { createGraphQLRequest } from "../api/utils/httpRequest"
import { EventDataResponse, EventSendRequestData } from "../graphql/types"
import { EventContext, Payload } from "./types"

const EVENT_POLLING_INTERVAL = 100

export const createEventRequest = <K extends keyof Payload>(
    type: K,
    ctx: EventContext,
    payload?: Payload[K],
    version?: number
): EventSendRequestData => {
    return {
        type: type,
        accountId: ctx.accountId,
        locationId: ctx.locationId,
        terminalId: ctx.terminalId,
        payload: payload ? JSON.stringify(payload) : null,
        correlationId: ctx.correlationId,
        aggregateType: ctx.aggregateType,
        aggregateId: ctx.aggregateId,
        version: version ?? null,
    }
}

export const sendEvent = async (headers: Record<string, any>, data: EventSendRequestData): Promise<void> => {
    await createGraphQLRequest({
        headers: headers,
        body: {
            query: `
                 mutation eventSend($data: EventSendRequestData!) {
                    eventSend(data: $data) {
                      success
                    }
                }
            `,
            operationName: "eventSend",
            variables: {
                data: data,
            },
        },
    })
}

export const getEventData = async (headers: Record<string, any>, id: string): Promise<Maybe<EventDataResponse>> => {
    const response = await createGraphQLRequest({
        headers: headers,
        body: {
            query: `
				query eventData($correlationId: String!) { 
					eventData(correlationId: $correlationId) { 
						id
						timestamp
						success 
						data
					}
				}
            `,
            operationName: "eventData",
            variables: {
                correlationId: id,
            },
        },
    })
    return response?.data?.eventData
}

export const requestEvent = async (
    headers: Record<string, any>,
    data: EventSendRequestData,
    timeout: number = 30000
): Promise<EventDataResponse> => {
    if (!data.correlationId) {
        // TODO maybe generate correlation id here?
        throw new Error("Correlation id is required")
    }
    const start = Date.now()
    let tick = 0
    await sendEvent(headers, data)
    while (true) {
        tick++
        if (Date.now() - start > timeout) {
            throw new Error("Event request timeout")
        }
        const eventData = await getEventData(headers, data.correlationId)
        if (eventData) {
            if (eventData.success) {
                return eventData
            }
            throw new Error(
                eventData.data?.message ||
                    eventData.data?.error?.message ||
                    eventData.data?.error ||
                    eventData.data?.payload?.message ||
                    (eventData.data?.messages as string[])?.join(", ") ||
                    "Event request failed"
            )
        }
        await sleep(EVENT_POLLING_INTERVAL * tick)
    }
}

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))
